import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { IImportProgressStepProps } from '../../types';
import { T_KEYS } from '../../constants';
import {
  ProgressCard,
  ProgressIcon,
  ProgressTitle,
  ProgressDescription,
  ProgressBarContainer,
  ProgressBarFill,
  NotificationBanner,
  ButtonsRow,
  PrimaryButton,
  SecondaryButton,
} from '../../styles';

const ImportProgressStep: FC<IImportProgressStepProps> = memo(({
  importStatus,
  onGoToDashboard,
  onStayHere,
  isRtl,
}) => {
  const { t } = useTranslation();

  const progress = importStatus?.progress || 0;
  const isCompleted = importStatus?.status === 'completed';
  const isFailed = importStatus?.status === 'failed';

  return (
    <ProgressCard $isRtl={isRtl}>
      <ProgressIcon>
        {isCompleted ? '✅' : isFailed ? '❌' : '⏳'}
      </ProgressIcon>

      <ProgressTitle>
        {isCompleted
          ? t(T_KEYS.IMPORT_COMPLETED)
          : isFailed
            ? t(T_KEYS.IMPORT_FAILED)
            : t(T_KEYS.IMPORTING_MODAL_TITLE)}
      </ProgressTitle>

      <ProgressDescription>
        {isCompleted
          ? t(T_KEYS.IMPORT_COMPLETED_DESC, { count: importStatus?.importedProperties || 0 })
          : isFailed
            ? (importStatus?.error || t(T_KEYS.IMPORT_ERROR))
            : t(T_KEYS.IMPORTING_MODAL_DESC)}
      </ProgressDescription>

      {!isCompleted && !isFailed && (
        <ProgressBarContainer>
          <ProgressBarFill $progress={progress} />
        </ProgressBarContainer>
      )}

      {!isCompleted && !isFailed && (
        <NotificationBanner $isRtl={isRtl}>
          <span>🔔</span>
          <span>
            {t(T_KEYS.IMPORTING_MODAL_NOTIFICATION)}
          </span>
        </NotificationBanner>
      )}

      <ButtonsRow>
        <SecondaryButton onClick={onStayHere}>
          {t(T_KEYS.STAY_HERE)}
        </SecondaryButton>
        <PrimaryButton onClick={onGoToDashboard}>
          {t(T_KEYS.GO_TO_DASHBOARD)}
        </PrimaryButton>
      </ButtonsRow>
    </ProgressCard>
  );
});

ImportProgressStep.displayName = 'ImportProgressStep';

export default ImportProgressStep;
